<script setup lang="ts">
import type { ConsentVersions } from '@core/types'
import { ConsentRequestType } from '@core/types/clients'
import { computed, isNewerConsentVersion, useConsentClient, useDialogSequenceHold, useQuery, useUserInfo } from '#imports'

defineOptions({
    name: 'ConsentChangedContainer'
})

const { isLoggedIn } = useUserInfo()
const accepted = useConsentClient().getConsent({ lazy: true, server: false, enabled: isLoggedIn })
// The versions alone decide: the text is a content query, which loads the SQLite engine, so only the lazy dialog asks for it.
const versions = useQuery<ConsentVersions>('/api/public/content/consents?versions', {
    key: 'content:consent-versions',
    lazy: true,
    server: false,
    enabled: isLoggedIn,
})

const acceptedVersions = computed(() => {
    const result: Record<string, string> = {}

    for (const consent of accepted.data.value ?? []) {
        if (consent.type === ConsentRequestType.Value1) result['general-terms'] = consent.version
        if (consent.type === ConsentRequestType.Value2) result['privacy-policy'] = consent.version
    }

    return result
})

const showGeneralTerms = computed(() => isNewerConsentVersion('general-terms', acceptedVersions.value['general-terms'], versions.data.value?.['general-terms']))
const showPrivacyPolicy = computed(() => isNewerConsentVersion('privacy-policy', acceptedVersions.value['privacy-policy'], versions.data.value?.['privacy-policy']))

useDialogSequenceHold([accepted, versions], {
    step: () => showGeneralTerms.value ? 'consent-changed-terms' : 'consent-changed-privacy',
    when: () => showGeneralTerms.value || showPrivacyPolicy.value
})
</script>

<template>
    <LazyConsentChangedDialog
        v-if="showGeneralTerms || showPrivacyPolicy"
        :show-general-terms="showGeneralTerms"
        :show-privacy-policy="showPrivacyPolicy"
        :accepted-versions="acceptedVersions"
        @consent:refresh="accepted.refresh"
    />
</template>
